/**
 * @module resolve
 * @description Implements the configuration-driven adapter resolver registry.
 * The module bridges configured domain providers to concrete adapter factories
 * so runtime variants can resolve adapters without coupling to implementations.
 */

import type { AdminAdapter } from "./admin/interface";
import type { AiAdapter } from "./ai/interface";
import type { AuthAdapter } from "./auth/interface";
import { getDomainConfig } from "./config";
import type {
  CacheAdapter,
  DeployAdapter,
  EventsAdapter,
  QueueAdapter,
} from "./contracts";
import type { DbAdapter } from "./db/interface";
import { AdapterResolutionError } from "./errors";
import type { LoggerAdapter } from "./logger/interface";
import type { PaymentsAdapter } from "./payments/interface";
import type { FileStorageAdapter } from "./storage/interface";
import type { DomainAdapterConfig, ResolvableAdapterDomain } from "./types";

/**
 * Maps adapter domains to their specific interface types.
 * Used for type-safe adapter resolution without runtime type information.
 *
 * @example
 * // With D = 'db', this type resolves to DbAdapter
 * type MyAdapter = AdapterForDomain<'db'>;
 */
type AdapterForDomain<D extends ResolvableAdapterDomain> = D extends "auth"
  ? AuthAdapter
  : D extends "db"
    ? DbAdapter
    : D extends "ai"
      ? AiAdapter
      : D extends "storage"
        ? FileStorageAdapter
        : D extends "payments"
          ? PaymentsAdapter
          : D extends "admin"
            ? AdminAdapter
            : D extends "logger"
              ? LoggerAdapter
              : D extends "cache"
                ? CacheAdapter
                : D extends "queue"
                  ? QueueAdapter
                  : D extends "events"
                    ? EventsAdapter
                    : D extends "deploy"
                      ? DeployAdapter
                      : never;

/**
 * Factory function type for creating adapter instances.
 * Takes a domain configuration and returns the appropriate adapter implementation.
 */
type AdapterResolver<D extends ResolvableAdapterDomain> = (
  config: DomainAdapterConfig,
) => AdapterForDomain<D>;

/**
 * Global registry of adapter factory functions.
 * Keyed by 'domain:provider' format (e.g., 'db:convex', 'auth:better-auth').
 * Populated by concrete provider packages or app bootstrap code at
 * initialization time.
 *
 * @example
 * // After importing the downstream @geenius/db Convex provider
 * resolvers.get('db:convex') // Returns the Convex DB adapter factory
 */
const resolvers: {
  [K in ResolvableAdapterDomain]: Map<string, AdapterResolver<K>>;
} = {
  auth: new Map(),
  db: new Map(),
  ai: new Map(),
  storage: new Map(),
  payments: new Map(),
  admin: new Map(),
  logger: new Map(),
  cache: new Map(),
  queue: new Map(),
  events: new Map(),
  deploy: new Map(),
};

function isResolvableDomain(
  domain: unknown,
): domain is ResolvableAdapterDomain {
  return typeof domain === "string" && Object.hasOwn(resolvers, domain);
}

function getResolverMap<D extends ResolvableAdapterDomain>(
  domain: D,
): Map<string, AdapterResolver<D>> {
  if (!isResolvableDomain(domain)) {
    throw new AdapterResolutionError(
      `Unknown adapter resolver domain: ${String(domain)}`,
      "MISSING_CONFIG",
    );
  }
  return resolvers[domain];
}

/**
 * Register a factory function for a specific domain and provider combination.
 * Called by downstream adapter implementation packages or application
 * bootstrap code to make concrete providers available for resolution.
 *
 * @typeParam D - The adapter domain being registered
 * @param domain - The adapter domain (db, auth, ai, storage, payments, admin)
 * @param provider - The specific provider name (convex, stripe, anthropic, etc.)
 * @param resolver - Factory function that creates an adapter instance from config
 *
 * @example
 * ```ts
 * // In a downstream @geenius/db Convex provider module
 * import { registerResolver } from '@geenius/adapters';
 *
 * registerResolver('db', 'convex', () => appDbAdapter);
 *
 * // In a downstream payments provider module
 * import { registerResolver } from '@geenius/adapters';
 *
 * registerResolver('payments', 'stripe', (config) =>
 *   createStripePaymentsAdapter({ apiKey: config.apiKey })
 * );
 * ```
 *
 * @remarks
 * Registration should happen at module load time (top-level), not in functions.
 * Typically done in the main export file of the concrete provider package or
 * in the application adapter bootstrap module.
 */
export function registerResolver<D extends ResolvableAdapterDomain>(
  domain: D,
  provider: string,
  resolver: (config: DomainAdapterConfig) => AdapterForDomain<D>,
): void {
  getResolverMap(domain).set(provider, resolver);
}

/**
 * Get an adapter instance for a given domain using the current configuration.
 * The domain must be configured via configureAdapters() and a resolver
 * must be registered for the configured provider.
 *
 * @typeParam D - The adapter domain being requested
 * @param domain - The adapter domain to resolve
 * @returns An initialized adapter instance of the appropriate type
 * @throws {Error} If domain is not configured or no resolver is registered
 *
 * @example
 * ```ts
 * // Setup (at app startup)
 * import '@geenius/db/convex-provider'; // Registers the db:convex resolver
 * import './adapters/anthropic'; // Registers the ai:anthropic resolver
 *
 * configureAdapters({
 *   db: { provider: 'convex' },
 *   ai: { provider: 'anthropic', apiKey: process.env.ANTHROPIC_API_KEY },
 * });
 *
 * // Usage (anywhere in app)
 * const dbAdapter = resolveAdapter('db');
 * const users = await dbAdapter.query('users', {});
 *
 * const aiAdapter = resolveAdapter('ai');
 * const response = await aiAdapter.chat([{ role: 'user', content: 'Hello' }]);
 * ```
 *
 * @remarks
 * Resolving the same domain multiple times returns a fresh instance
 * (unless the adapter factory implements caching/singleton behavior).
 * For performance, consider caching the resolved adapter in your app.
 */
export function resolveAdapter<D extends ResolvableAdapterDomain>(
  domain: D,
): AdapterForDomain<D> {
  const resolverMap = getResolverMap(domain);
  // Get the configuration for this domain
  const config = getDomainConfig(domain);
  if (!config) {
    throw new AdapterResolutionError(
      `No configuration found for adapter domain: ${domain}`,
      "MISSING_CONFIG",
    );
  }

  // Look up the factory function for this domain:provider combination
  const resolver = resolverMap.get(config.provider);
  if (!resolver) {
    throw new AdapterResolutionError(
      `No resolver registered for ${domain}:${config.provider}. ` +
        `Call registerResolver('${domain}', '${config.provider}', factory) first.`,
      "MISSING_RESOLVER",
    );
  }

  // Call the factory function and return the configured adapter
  return resolver(config);
}

/**
 * Check if a resolver is registered for a specific domain and provider.
 * Useful for conditional logic around adapter availability.
 *
 * @param domain - The adapter domain
 * @param provider - The provider name
 * @returns True if a resolver is registered for this domain:provider, false otherwise
 *
 * @example
 * ```ts
 * if (hasResolver('ai', 'anthropic')) {
 *   // Anthropic is available, can safely use it
 *   const aiAdapter = resolveAdapter('ai');
 * }
 * ```
 */
export function hasResolver(
  domain: ResolvableAdapterDomain,
  provider: string,
): boolean {
  return getResolverMap(domain).has(provider);
}

/**
 * Clear all registered resolvers from the registry.
 * Primarily useful in testing to avoid state leakage between tests.
 *
 * @example
 * ```ts
 * beforeEach(() => {
 *   clearResolvers(); // Clean up from previous test
 *   // Re-register with test doubles
 *   registerResolver('db', 'mock', createMockDbAdapter);
 * });
 * ```
 */
export function clearResolvers(): void {
  for (const resolverMap of Object.values(resolvers)) {
    resolverMap.clear();
  }
}
